import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  message,
  Upload,
  Image,
  Space,
  List,
  Spin,
  Empty,
  Popconfirm,
  Divider,
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import RichTextEditor from '../components/RichTextEditor';
import { uploadFile } from '../services/fileService';
import {
  getMiniProgramUserConfig,
  saveMiniProgramUserConfig,
  MiniProgramBannerItem,
  MiniProgramUserConfig as MiniProgramUserConfigType,
} from '../services/miniProgramUserConfigService';

const { Title, Paragraph } = Typography;

type UserConfigTabKey = 'login' | 'agreement' | 'home' | 'partner' | 'support';

const userConfigTabLabels: Record<UserConfigTabKey, string> = {
  login: '登录页配置',
  agreement: '协议配置',
  home: '首页配置',
  partner: '成为伴宠师跳转配置',
  support: '客服与帮助配置',
};

const userConfigTabFieldMap: Record<UserConfigTabKey, (keyof MiniProgramUserConfigType)[]> = {
  login: ['loginLogo', 'loginLogoFileId', 'loginBrandName', 'loginTitle', 'loginDescription'],
  agreement: ['userAgreement', 'privacyPolicy', 'orderPurchaseAgreement'],
  home: ['homeBanners', 'advertisementBanners', 'enabled'],
  partner: ['partnerMiniProgramAppId', 'partnerMiniProgramPath'],
  support: ['supportPhone', 'supportWechat', 'supportHelpText', 'supportFaqs'],
};

const createDefaultValues = (): MiniProgramUserConfigType => ({
  loginLogo: '',
  loginLogoFileId: '',
  loginBrandName: '',
  loginTitle: '',
  loginDescription: '',
  userAgreement: '',
  privacyPolicy: '',
  homeBanners: [],
  advertisementBanners: [],
  orderPurchaseAgreement: '',
  supportPhone: '',
  supportWechat: '',
  supportHelpText: '',
  supportFaqs: [],
  partnerMiniProgramAppId: '',
  partnerMiniProgramPath: '',
  enabled: true,
});

const normalizeBanners = (banners: MiniProgramBannerItem[] = []) =>
  banners.map((item, index) => ({
    url: item.url,
    fileId: item.fileId || '',
    sort: index + 1,
    path: item.path || '',
  }));

const isRichTextEmpty = (value?: string) => {
  if (!value) {
    return true;
  }
  return value.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, '').trim() === '';
};

const MiniProgramUserConfig: React.FC = () => {
  const [form] = Form.useForm<MiniProgramUserConfigType>();
  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [advertisementBannerUploading, setAdvertisementBannerUploading] = useState(false);
  const [logoPreviewVisible, setLogoPreviewVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<UserConfigTabKey>('login');

  const loadConfig = useCallback(async () => {
    setInitialLoading(true);
    try {
      const res = await getMiniProgramUserConfig();
      const data = res.data || createDefaultValues();
      form.setFieldsValue({
        ...createDefaultValues(),
        ...data,
        homeBanners: normalizeBanners(data.homeBanners || []),
        advertisementBanners: normalizeBanners(data.advertisementBanners || []),
        supportFaqs: Array.isArray(data.supportFaqs) ? data.supportFaqs : [],
      });
    } catch {
      message.error('加载配置失败');
      form.setFieldsValue(createDefaultValues());
    } finally {
      setInitialLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const logoUrl = Form.useWatch('loginLogo', form);
  const watchedBanners = Form.useWatch('homeBanners', form);
  const watchedAdvertisementBanners = Form.useWatch('advertisementBanners', form);

  const orderedBanners = useMemo(() => normalizeBanners(watchedBanners || []), [watchedBanners]);
  const orderedAdvertisementBanners = useMemo(
    () => normalizeBanners(watchedAdvertisementBanners || []),
    [watchedAdvertisementBanners]
  );

  const updateBanners = (fieldName: 'homeBanners' | 'advertisementBanners', nextBanners: MiniProgramBannerItem[]) => {
    form.setFieldValue(fieldName, normalizeBanners(nextBanners));
  };

  const handleLogoUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setLogoUploading(true);
    try {
      const res = await uploadFile(file as File, 'mini_program_user_login_logo');
      form.setFieldsValue({
        loginLogo: res.data.url,
        loginLogoFileId: res.data.id,
      });
      message.success('Logo 上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Logo 上传失败');
      onError?.(error);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleBannerUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setBannerUploading(true);
    try {
      const res = await uploadFile(file as File, 'mini_program_user_banner');
      const nextBanners = [
        ...orderedBanners,
        {
          url: res.data.url,
          fileId: res.data.id,
          sort: orderedBanners.length + 1,
        },
      ];
      updateBanners('homeBanners', nextBanners);
      message.success('轮播图上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '轮播图上传失败');
      onError?.(error);
    } finally {
      setBannerUploading(false);
    }
  };

  const handleAdvertisementBannerUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setAdvertisementBannerUploading(true);
    try {
      const res = await uploadFile(file as File, 'mini_program_user_advertisement_banner');
      const nextBanners = [
        ...orderedAdvertisementBanners,
        {
          url: res.data.url,
          fileId: res.data.id,
          sort: orderedAdvertisementBanners.length + 1,
        },
      ];
      updateBanners('advertisementBanners', nextBanners);
      message.success('广告图片上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '广告图片上传失败');
      onError?.(error);
    } finally {
      setAdvertisementBannerUploading(false);
    }
  };

  const moveBanner = (
    fieldName: 'homeBanners' | 'advertisementBanners',
    banners: MiniProgramBannerItem[],
    index: number,
    direction: 'up' | 'down'
  ) => {
    const nextBanners = [...banners];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= nextBanners.length) {
      return;
    }
    [nextBanners[index], nextBanners[targetIndex]] = [nextBanners[targetIndex], nextBanners[index]];
    updateBanners(fieldName, nextBanners);
  };

  const removeBanner = (
    fieldName: 'homeBanners' | 'advertisementBanners',
    banners: MiniProgramBannerItem[],
    index: number
  ) => {
    updateBanners(fieldName, banners.filter((_, currentIndex) => currentIndex !== index));
  };

  const updateBannerPath = (
    fieldName: 'homeBanners' | 'advertisementBanners',
    banners: MiniProgramBannerItem[],
    index: number,
    path: string
  ) => {
    const nextBanners = banners.map((item, currentIndex) => (
      currentIndex === index ? { ...item, path } : item
    ));
    updateBanners(fieldName, nextBanners);
  };

  const saveConfig = async (values: MiniProgramUserConfigType) => {
    const payload = {
      ...values,
      homeBanners: normalizeBanners(values.homeBanners || []),
      advertisementBanners: normalizeBanners(values.advertisementBanners || []),
    };

    setSubmitting(true);
    try {
      await saveMiniProgramUserConfig(payload);
      message.success('保存成功');
      await loadConfig();
    } catch {
      message.error('保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values: MiniProgramUserConfigType) => {
    await saveConfig(values);
  };

  const handleSaveCurrentTab = async () => {
    try {
      await form.validateFields(userConfigTabFieldMap[activeTab]);
      await saveConfig(form.getFieldsValue(true) as MiniProgramUserConfigType);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error('保存失败');
    }
  };

  const renderBannerList = (
    fieldName: 'homeBanners' | 'advertisementBanners',
    banners: MiniProgramBannerItem[],
    titlePrefix: string
  ) => (
    banners.length > 0 ? (
      <List
        dataSource={banners}
        renderItem={(item, index) => (
          <List.Item
            actions={[
              <Button
                key="up"
                type="link"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => moveBanner(fieldName, banners, index, 'up')}
              >
                上移
              </Button>,
              <Button
                key="down"
                type="link"
                icon={<ArrowDownOutlined />}
                disabled={index === banners.length - 1}
                onClick={() => moveBanner(fieldName, banners, index, 'down')}
              >
                下移
              </Button>,
              <Popconfirm
                key="delete"
                title={`确定删除该${titlePrefix}？`}
                onConfirm={() => removeBanner(fieldName, banners, index)}
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              avatar={<Image src={item.url} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 8 }} />}
              title={`${titlePrefix} ${index + 1}`}
              description={
                <Space orientation="vertical" size={8} style={{ width: '100%' }}>
                  <span>{item.url}</span>
                  {fieldName === 'advertisementBanners' ? (
                    <Input
                      value={item.path || ''}
                      placeholder="可选：请输入点击广告后跳转的小程序页面路径，例如 /pages-main/coupon/index；留空则不跳转"
                      onChange={event => updateBannerPath(fieldName, banners, index, event.target.value)}
                    />
                  ) : null}
                </Space>
              }
            />
          </List.Item>
        )}
      />
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`暂无${titlePrefix}`} />
    )
  );

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
          <div>
            <Title level={2}>用户小程序配置</Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              配置用户端小程序登录页、首页展示和下单协议等内容。
            </Paragraph>
          </div>
          <Button type="primary" icon={<SaveOutlined />} loading={submitting} onClick={handleSaveCurrentTab}>
            保存{userConfigTabLabels[activeTab]}
          </Button>
        </div>
      </div>

      <div className="page-content">
        <Spin spinning={initialLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={createDefaultValues()}
          >
            <Tabs activeKey={activeTab} onChange={key => setActiveTab(key as UserConfigTabKey)} style={{ marginBottom: 24 }}>
              <Tabs.TabPane tab="登录页配置" key="login">
                <Card title="登录页配置">
              <Form.Item
                name="loginLogo"
                label="登录 Logo"
                rules={[{ required: true, message: '请上传登录 Logo' }]}
              >
                <Input placeholder="请上传登录 Logo" readOnly />
              </Form.Item>

              <Form.Item name="loginLogoFileId" hidden>
                <Input />
              </Form.Item>

              <Form.Item>
                <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                  <Upload accept="image/*" showUploadList={false} customRequest={handleLogoUpload}>
                    <Button loading={logoUploading} icon={<UploadOutlined />}>
                      上传 Logo
                    </Button>
                  </Upload>
                  {logoUrl ? (
                    <Image
                      src={logoUrl}
                      alt="登录 Logo"
                      width={120}
                      style={{ borderRadius: 8, border: '1px solid #f0f0f0', cursor: 'pointer' }}
                      preview={{
                        open: logoPreviewVisible,
                        onOpenChange: setLogoPreviewVisible,
                      }}
                    />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无 Logo" />
                  )}
                </Space>
              </Form.Item>

              <Form.Item
                name="loginBrandName"
                label="登录商标名字"
                rules={[{ required: true, message: '请输入登录商标名字' }]}
              >
                <Input placeholder="请输入登录商标名字" />
              </Form.Item>

              <Form.Item name="loginTitle" label="登录标题" rules={[{ required: true, message: '请输入登录标题' }]}>
                <Input placeholder="请输入登录标题" />
              </Form.Item>

              <Form.Item
                name="loginDescription"
                label="登录说明"
                rules={[{ required: true, message: '请输入登录说明' }]}
              >
                <Input.TextArea rows={4} placeholder="请输入登录说明" />
              </Form.Item>
                </Card>
              </Tabs.TabPane>

              <Tabs.TabPane tab="协议配置" key="agreement">
                <Card title="协议配置">
              <Form.Item
                name="userAgreement"
                label="登录用户协议"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (isRichTextEmpty(value)) {
                        throw new Error('请输入登录用户协议内容');
                      }
                    },
                  },
                ]}
              >
                <RichTextEditor placeholder="请输入登录用户协议内容" />
              </Form.Item>

              <Form.Item
                name="privacyPolicy"
                label="隐私政策"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (isRichTextEmpty(value)) {
                        throw new Error('请输入隐私政策内容');
                      }
                    },
                  },
                ]}
              >
                <RichTextEditor placeholder="请输入隐私政策内容" />
              </Form.Item>

              <Form.Item
                name="orderPurchaseAgreement"
                label="订单购买协议"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (isRichTextEmpty(value)) {
                        throw new Error('请输入订单购买协议内容');
                      }
                    },
                  },
                ]}
              >
                <RichTextEditor placeholder="请输入订单购买协议内容" />
              </Form.Item>
                </Card>
              </Tabs.TabPane>

              <Tabs.TabPane tab="首页配置" key="home">
                <Card title="首页配置">
              <Form.Item
                name="homeBanners"
                hidden
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value || value.length === 0) {
                        throw new Error('请至少上传 1 张轮播图');
                      }
                    },
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="首页轮播图"
                required
                validateStatus={orderedBanners.length === 0 ? 'error' : ''}
                help={
                  orderedBanners.length === 0
                    ? '请至少上传 1 张轮播图。用户小程序首页展示尺寸：750rpx × 600rpx，建议按 5:4 比例上传'
                    : '支持多图上传，可调整顺序。用户小程序首页展示尺寸：750rpx × 600rpx，建议按 5:4 比例上传'
                }
              >
                <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                  <Upload accept="image/*" showUploadList={false} customRequest={handleBannerUpload} multiple>
                    <Button loading={bannerUploading} icon={<UploadOutlined />}>
                      上传轮播图
                    </Button>
                  </Upload>

                  {renderBannerList('homeBanners', orderedBanners, '轮播图')}
                </Space>
              </Form.Item>

              <Form.Item name="advertisementBanners" hidden>
                <Input />
              </Form.Item>

              <Form.Item
                label="广告图片轮播图"
                help="用于用户小程序首页底部广告位，支持多图上传，可调整顺序。展示尺寸：690rpx × 300rpx，建议按 23:10 比例上传"
              >
                <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    customRequest={handleAdvertisementBannerUpload}
                    multiple
                  >
                    <Button loading={advertisementBannerUploading} icon={<UploadOutlined />}>
                      上传广告图片
                    </Button>
                  </Upload>

                  {renderBannerList('advertisementBanners', orderedAdvertisementBanners, '广告图片')}
                </Space>
              </Form.Item>

              <Form.Item name="enabled" label="启用状态" valuePropName="checked">
                <Switch />
              </Form.Item>
                </Card>
              </Tabs.TabPane>

              <Tabs.TabPane tab="成为伴宠师跳转配置" key="partner">
                <Card title="成为伴宠师跳转配置">
              <Form.Item
                name="partnerMiniProgramAppId"
                label="目标小程序 AppID"
                tooltip="填写服务人员端小程序 AppID。留空时用户端点击“成为伴宠师”会提示暂未开放"
              >
                <Input placeholder="请输入目标小程序 AppID，例如 wx1234567890abcdef" />
              </Form.Item>

              <Form.Item
                name="partnerMiniProgramPath"
                label="目标小程序路径"
                tooltip="可选。填写后会跳转到指定页面；留空则打开目标小程序首页"
              >
                <Input placeholder="请输入目标小程序路径，例如 pages/index/index" />
              </Form.Item>
                </Card>
              </Tabs.TabPane>

              <Tabs.TabPane tab="客服与帮助配置" key="support">
                <Card title="客服与帮助配置">
              <Form.Item name="supportPhone" label="客服电话">
                <Input placeholder="请输入客服电话" />
              </Form.Item>

              <Form.Item name="supportWechat" label="客服微信">
                <Input placeholder="请输入客服微信号" />
              </Form.Item>

              <Form.Item name="supportHelpText" label="需要帮助文案">
                <Input.TextArea rows={4} placeholder="请输入客服与帮助页面顶部说明文案" />
              </Form.Item>

              <Divider style={{ marginTop: 0 }}>
                常见问题
              </Divider>

              <Form.List name="supportFaqs">
                {(fields, { add, remove }) => (
                  <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                    {fields.map((field, index) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={`常见问题 ${index + 1}`}
                        extra={
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          >
                            删除
                          </Button>
                        }
                      >
                        <Form.Item
                          {...field}
                          name={[field.name, 'question']}
                          label="问题"
                          rules={[{ required: true, message: '请输入问题' }]}
                        >
                          <Input placeholder="请输入常见问题标题" />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'answer']}
                          label="答案"
                          rules={[{ required: true, message: '请输入答案' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input.TextArea rows={4} placeholder="请输入常见问题答案" />
                        </Form.Item>
                      </Card>
                    ))}

                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ question: '', answer: '' })}>
                      新增常见问题
                    </Button>
                  </Space>
                )}
              </Form.List>
                </Card>
              </Tabs.TabPane>
            </Tabs>

          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default MiniProgramUserConfig;
